import {
  IBalanceInquiryProps,
  showBalanceInquiryContainer,
} from 'src/balance-inquiry/balance-inquiry';
import {
  IBalanceInquiryModalProps,
  showBalanceInquiryModal,
} from 'src/balance-inquiry/balance-inquiry-modal';
import { restorePage } from 'src/common/components/emerald-home-page/emerald-home-page';
import { withSpinner } from 'src/common/components/with-spinner/with-spinner';
import { CardType } from 'src/common/enumerations/card-type';
import { DeviceType } from 'src/common/enumerations/device-type';
import { EntryMethod } from 'src/common/enumerations/entry-method';
import { FailedToPrintReceipt, NoPrinterConnection } from 'src/common/helpers/error-code';
import XLRLogger from 'src/common/helpers/logger';
import { IItemInputData } from 'src/common/interfaces/IItemInputData';
import { store } from 'src/common/redux/configure-store';
import { BalanceInquiryActions } from 'src/common/redux/reducers/balance-inquiry-reducer';
import {
  EpsData,
  parseElectronicPaymentData,
  PaymentMessage,
  CardDetailsContext,
} from 'src/devices/payment/dtos/eps-data';
import {
  balanceInquiryFromPinPad,
  beginPaymentSession,
  cancelPinpadOperation,
  endPaymentSession,
  getCardDetailsFromPinPad,
  getPrescriptionInquiryFromPinPad,
  inPaymentSession,
} from 'src/devices/payment/pinpad-operations';
import { PmiStatusCodes } from 'src/devices/payment/pmi-status-codes-enums';
import { disableScanner } from 'src/devices/scanner-operations';
import { getDataInformation } from 'src/home-page/business-logic/get-data-infomation';
import { voidEmptyTransaction } from 'src/home-page/business-logic/void-transaction';
import { initiateTransaction } from 'src/items-list/business-logic/initiate-transaction';
import { popupMessage } from 'src/messages/business-logic/popup-message';
import { EwicReceiptType } from 'src/tendering/enumerations/ewic-receipt-type';
import { EwicPrintDTO } from 'src/tendering/interfaces/ewic-print.dto';
import { GiftCardBalanceInquiryDto } from 'src/tendering/interfaces/gift-card-balance-inquiry-dto';
import { getCurrentXLRConfig } from 'src/xlr-configuration/xlr-config';
import showPinpadDisconnectedModal from '../../common/components/pinpad-disconnected-modal';
import { hasCardData, resetCardData, swipeAhead } from '../../devices/payment/get-card-details';
import {
  entryMethodStringConvert,
  getMiddleDigits,
  handleBalanceInquiryWithEwicService,
  handleBalanceInquiryWithTransactionService,
  parseGiftCardBalanceInquiryReceipt,
  printBalanceInquiry,
  printEwicBalanceInquirySE,
  validateEwicData,
} from './handle-balance-inquiry-helper';

export interface EwicBalanceInquiryDTO {
  stateIdentifierCode: string;
  pan: string;
  expirationDate: string;
  authorizationCode: string;
  isSaleMode: boolean;
  prescriptionLineItems: EwicPrescriptionLineItem[] | undefined;
  referenceNumber: string;
  declineCode?: string;
  declineReason?: string;
}

export interface EwicPrescriptionLineItem {
  categoryCode: string;
  subcategoryCode: string;
  benefitQuantity: number;
  benefitQuantityDecimal: number;
}

const closeBalanceInquiryWindow = (): void => restorePage();

const onKeypadSubmit = async (data: string): Promise<void> => {
  store.dispatch(BalanceInquiryActions.setIsProcessing(true));
  await getCardExternalId(data, EntryMethod.Keyed)
    .then(balanceInquiry)
    .catch(popupMessage);
};

export const onScan = async (data: string): Promise<void> => {
  store.dispatch(BalanceInquiryActions.setIsProcessing(true));
  await getCardExternalId(data, EntryMethod.Scanned)
    .then(balanceInquiry)
    .catch(popupMessage);
};

const getCardExternalId = async (data: string, entryMethod: EntryMethod): Promise<string> => {
  // cancel GetCardDetails - need to find way to tell if we cancelled or customer did on the pinpad
  await cancelPinpadOperation();

  const inputData: IItemInputData = {
    itemId: data,
    entryMethod,
  };

  return getDataInformation(inputData)
    .then(async response => {
      if (response.onlineItemExternalId) {
        return response.onlineItemExternalId;
      } else {
        return data;
      }
    })
    .catch(async err => {
      await onFinish();
      throw err;
    });
};

const onFinish = async (): Promise<void> => {
  if (inPaymentSession()) {
    await cancelPinpadOperation();
  }
  await endPaymentSession();
  if (!hasCardData() && store.getState().itemList.ejItems.length > 0) {
    await beginPaymentSession();
    resetCardData();
    swipeAhead();
  }
  await voidEmptyTransaction(false);
  store.dispatch(BalanceInquiryActions.setIsProcessing(false));
  closeBalanceInquiryWindow();
};

const setupBalanceInquirySwipe = async (): Promise<void> => {
  let modalProps: IBalanceInquiryModalProps;
  try {
    if (!store.getState().transaction.transactionIdExists) {
      await initiateTransaction();
    }
    if (inPaymentSession()) {
      await cancelPinpadOperation();
      await endPaymentSession();
    }
    await beginPaymentSession();
    let epsData: EpsData;

    const BALANCE_INQUIRY_GET_CARD_DETAIL_AMOUNT = 1;
    try {
      epsData = await getCardDetailsFromPinPad(
        BALANCE_INQUIRY_GET_CARD_DETAIL_AMOUNT,
        BALANCE_INQUIRY_GET_CARD_DETAIL_AMOUNT,
        undefined,
        CardDetailsContext.NonPayment,
      );
    } catch (err) {
      // if GetCardDetails is cancelled
      if (err?.statusCode === PmiStatusCodes.Cancelled) {
        return;
      } else {
        popupMessage(err);
        return;
      }
    }

    // disable scanner since getCardDetails was successful - they swiped
    disableScanner();
    store.dispatch(BalanceInquiryActions.setIsProcessing(true));

    if (epsData.card.cardType?.toUpperCase() === CardType.Ewic) {
      await balanceInquiryForEwic(epsData.paymentTranID);
    } else if (
      epsData.card.cardType?.toUpperCase() === CardType.GiftCard ||
      epsData.card.cardType?.toUpperCase() === CardType.Ebtcash ||
      epsData.card.cardType?.toUpperCase() === CardType.Ebtfoodstamp
    ) {
      await balanceInquiry(
        undefined,
        epsData.paymentTranID,
        epsData.sessionTranID,
        epsData.card.cardType?.toUpperCase(),
      );
    } else {
      await onFinish();
      modalProps = {
        success: false,
        showBalance: false,
        wrongType: true,
      };
      showBalanceInquiryModal(modalProps);
    }
  } catch (err) {
    await onFinish();
    XLRLogger.error({
      message: 'Balance inquiry failed',
      source: 'handle-balance-inquiry.ts',
      data: err,
    });
    eWicFinishOperations(false);
  }
};

const balanceInquiryForEwic = async (paymentTranID: string): Promise<void> => {
  // getPrescriptionFromPinPad will not work against our current payment host
  const modalProps: IBalanceInquiryModalProps = {
    success: false,
    showBalance: false,
  };
  let prescriptionInquiryResponse: PaymentMessage;
  try {
    prescriptionInquiryResponse = await getPrescriptionInquiryFromPinPad(paymentTranID);
  } catch (err) {
    const declineCode = err.statusCode;
    const declineReason = err.message;
    const eWicPrintDTO: EwicPrintDTO = {
      utcDateTime: new Date().toUTCString(),
      cashierName: store.getState().user.displayName,
      cashierUsername: store.getState().user.userName,
      receiptType: EwicReceiptType.BALANCEINQUIRY,
      declineCode: declineCode?.toString(),
      declineReason: declineReason,
      inquiryReferenceId: paymentTranID || '',
      itemPrescriptions: [],
    };
    await printEwicBalanceInquirySE(eWicPrintDTO);
    await onFinish();
    showBalanceInquiryModal(modalProps);
    return;
  }
  validateEwicData(prescriptionInquiryResponse);
  const parsedData = parseElectronicPaymentData(prescriptionInquiryResponse);

  const { balancesWithEwicService: requiresEwicService } = getCurrentXLRConfig();
  if (prescriptionInquiryResponse && prescriptionInquiryResponse.cardDetails && parsedData) {
    const cardDetails = prescriptionInquiryResponse.cardDetails[0] || undefined;

    const balanceHandler = requiresEwicService
      ? handleBalanceInquiryWithEwicService
      : handleBalanceInquiryWithTransactionService;

    const status: boolean | void = await balanceHandler({
      cardDetails,
      epsData: parsedData,
      transactionDateTimeUTC:
        prescriptionInquiryResponse.transactionDateTimeUTC || new Date().toUTCString(),
    }).catch(popupMessage);

    if (status) {
      eWicFinishOperations(status);
    } else {
      if (
        !store
          .getState()
          .deviceStatus.devices.find(d => d.deviceType === DeviceType.Printer && d.initialized)
      ) {
        modalProps.errorCode = NoPrinterConnection;
      }

      await onFinish();
      showBalanceInquiryModal(modalProps);
    }
  }
};

const balanceInquiry = async (
  externalId?: string,
  paymentTranId?: string,
  sessionTranId?: string,
  cardType?: string,
): Promise<void> => {
  let modalProps: IBalanceInquiryModalProps = {
    success: false,
    showBalance: false,
    wrongType: false,
  };
  let response: PaymentMessage;
  try {
    response = await balanceInquiryFromPinPad(externalId, paymentTranId, sessionTranId);
  } catch (err) {
    const declineCode = err.statusCode;
    const declineReason = err.message;
    const giftCardBalanceInquiryDto: GiftCardBalanceInquiryDto = {
      authorizationDateTime: new Date().toUTCString(),
      declineCode: declineCode?.toString(),
      declineReason: declineReason,
      customerReceipt: '',
    };
    await printBalanceInquiry(giftCardBalanceInquiryDto);
    await onFinish();
    showBalanceInquiryModal(modalProps);
    return;
  }
  if (response) {
    const cardDetails = response.cardDetails?.[0] || {};
    const receipts = response.receipts ?? '';
    const cardNumber = cardType === CardType.GiftCard ? cardDetails.pan : externalId; //AC: only Gift Card has unmasked PAN
    const MIN_CARD_LENGTH = 10;
    const cardMiddleDigits = getMiddleDigits(cardDetails.cardLength ?? MIN_CARD_LENGTH);
    const giftCardBalanceInquiryDto: GiftCardBalanceInquiryDto = {
      remainingBalance: Number(response.amounts?.cardBalance || '0') || 0,
      // on BFF side authorizationExternalId is used for maskedAccountNumber
      // only maskedAccountNumber OR pan (cardNumber) should be sent, not both.
      // giftcards should have unmasked PAN, other card types need to build the masked card number below
      authorizationExternalId:
        cardType !== CardType.GiftCard
          ? `${cardDetails.firstSix}${cardMiddleDigits}${cardDetails.lastFour}`
          : undefined,
      initialBalance: Number(response.amounts?.cardBalance || '0') || 0,
      authorizationCode: response.paymentDetails?.authCode,
      inquiryReferenceId: cardDetails.paymentTranID,
      signatureRequiredFlag: false,
      verifiedByPinFlag: false,
      authorizationDateTime: new Date().toUTCString(),
      customerReceipt: parseGiftCardBalanceInquiryReceipt(receipts || []),
      entryMethod: entryMethodStringConvert(cardDetails.entryMethod || ''),
      scanData: '170',
      transactionExternalId: store.getState().transaction.transactionId,
      isSaleMode: true,
      pan: cardNumber,
      authorizationUTCDateTime: response.transactionDateTimeUTC ?? new Date().toUTCString(),
    };

    const status = await printBalanceInquiry(giftCardBalanceInquiryDto);

    if (status) {
      modalProps = {
        success: true,
        showBalance: true,
        remainingBalance: Number(response.amounts?.cardBalance || '0'),
      };
    } else {
      modalProps = {
        success: false,
        showBalance: true,
        remainingBalance: Number(response.amounts?.cardBalance || '0'),
        errorCode: FailedToPrintReceipt,
      };
    }
  } else {
    modalProps = {
      success: false,
      showBalance: false,
      wrongType: true,
    };
  }
  await onFinish();
  showBalanceInquiryModal(modalProps);
};

const eWicFinishOperations = async (status: boolean): Promise<void> => {
  await onFinish();
  const modalProps = {
    success: status,
    showBalance: false,
  };
  showBalanceInquiryModal(modalProps);
};

const onCancel = () => withSpinner(() => onFinish());

const props: IBalanceInquiryProps = {
  onCancel: onCancel,
  onKeypadSubmit: onKeypadSubmit,
};

export const handleBalanceInquiry = async (): Promise<void> => {
  if (store.getState().pinPad && store.getState().pinPad.isPinpadConfigured) {
    showBalanceInquiryContainer(props, onScan);
    await setupBalanceInquirySwipe();
  } else {
    showPinpadDisconnectedModal();
  }
};
